import axios from 'axios';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import { MdDelete, MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { Tooltip } from 'react-tooltip';
import { RiPencilFill } from 'react-icons/ri';
import { ToastContainer, toast } from 'react-toastify';
import ReactPaginate from 'react-paginate';

import constant from '../../utils/Constant';
import Loader from '../partials/Loader';
import config from '../../config/config';

const {
    ALL_INFO_END_POINT,
    BOOLEAN_TRUE: trueValue,
    BOOLEAN_FALSE: falseValue,
    NULL,
    EMPTY_STRING: emtpyString,
    STATUS_200,
    LOGOUT,
    ZERO,
    REQUESTED_RECORD,
    ONE,
    TWO,
    STATUS_401
} = constant;

const AllInformation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const page = location.state?.page || ZERO;

    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(ONE);
    const [currentPage, setCurrentPage] = useState(page);
    const modalRef = useRef(NULL);
    const [updateModal, setUpdateModal] = useState(falseValue);
    const [confirmationModal, setConfirmationModal] = useState(falseValue);
    const [createmodal, setCreatemodal] = useState(falseValue);
    const [loading, setLoading] = useState(falseValue);
    const [disable, setDisable] = useState(falseValue);
    const [content, setContent] = useState(emtpyString);
    const [infoID, setInfoID] = useState(emtpyString);
    const [createFormData, setCreateFormData] = useState({
        name: emtpyString,
        priority: emtpyString,
        status: emtpyString
    });
    const [updateFormData, setUpdateFormData] = useState({});

    const editor = useRef(NULL);

    // * get token from local storage
    const token = localStorage.getItem('vendor_super_token');

    // * execute all information request api
    const executeAllInfoApi = useCallback(async () => {
        try {
            const apiUrl = config.appBaseUrl + ALL_INFO_END_POINT;

            // * execute get request
            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // * api response
            const result = response.data;

            // * if response is success
            if (result.code === STATUS_200 && result.status) {
                setData(result.data);
                setTotalPages(Math.ceil(result.length / REQUESTED_RECORD));
            }
        } catch (error) {
            toast.error(error.response?.data?.message);

            // * if token is expired
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        }
    }, [navigate, token]);

    // * execute all information request api
    useEffect(() => {
        executeAllInfoApi();
    }, [executeAllInfoApi, token, page]);

    // * set total pages
    useEffect(() => {
        setTotalPages(Math.ceil(data.length / REQUESTED_RECORD));
    }, [data]);

    // * paginated data
    const paginatedData = data.slice(
        currentPage * REQUESTED_RECORD,
        (currentPage + ONE) * REQUESTED_RECORD
    );

    // * handle page change
    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
        navigate('/information/all-information', { state: { page: selected } });
    };

    // * handle paste 
    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        if (editor.current) {
            editor.current.selection?.insertHTML(text);
        }
    };

    // * editor config
    const editorConfig = useMemo(() => ({
        readonly: false,
        toolbarAdaptive: false,
        buttons: [
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'ul',
            'ol',
            'align',
            'link',
            'image',
            'video',
            'font',
            'fontsize',
            'brush',
            'paragraph',
            'hr',
        ],
        events: {
            afterPaste: handlePaste
        },
        disablePlugins: ['pasteHTML']
    }), []);
 
    //* handle click outside
    const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            setUpdateModal(falseValue);
            setCreatemodal(falseValue);
            setConfirmationModal(falseValue);
        }
    };

    //* handle click outside
    useEffect(() => {
        if (updateModal || createmodal || confirmationModal) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [updateModal, createmodal, confirmationModal]);

    // * handle modal open and close
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Tab' && (updateModal || createmodal || confirmationModal)) {
                const focusableElements = document.querySelectorAll(
                    '.modal input, .modal button, .modal select, .modal textarea, .modal a'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                //* prevent tabbing outside the modal
                if (event.shiftKey) {
                    //* shift + tab (focus previous element)
                    if (document.activeElement === firstElement) {
                        lastElement.focus();  //* loop to the last element
                        event.preventDefault();
                    }
                } else {
                    //* tab (focus next element)
                    if (document.activeElement === lastElement) {
                        firstElement.focus();  //* loop to the first element
                        event.preventDefault();
                    }
                }
            }
        };

        //* check when modal is open
        if (updateModal || createmodal || confirmationModal ) {
            document.body.classList.add('modal-open');
            document.addEventListener('keydown', handleKeyDown);
            //* focus the first element inside the modal when it opens
            const firstFocusable = document.querySelector('.modal button, .modal input, .modal select, .modal a');
            if (firstFocusable) {
                firstFocusable.focus();
            }
        } else {
            document.body.classList.remove('modal-open');
            document.removeEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.body.classList.remove('modal-open');
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [updateModal, createmodal, confirmationModal]);

    // * execute create info
    const executeCreateInformationApi = async (event) => {
        event.preventDefault();

        //* name validation
        if (!createFormData.name) {
            toast.error('Category is required');
            return;
        }

        //* priority validation
        if (!createFormData.priority) {
            toast.error('Priority is required');
            return;
        }

        //* status validation
        if (createFormData.status === emtpyString) {
            toast.error('Status is required');
            return;
        }

        setLoading(trueValue);
        setDisable(trueValue);

        //* send data to request
        const data = {
            description: content,
            name: createFormData.name,
            status: createFormData.status,
            priority: Number(createFormData.priority)
        }

        try {
            const apiUrl = config.appBaseUrl + ALL_INFO_END_POINT;

            //* post request
            const response = await axios.post(apiUrl,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            //* response success
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                toast.success(result.message);
                executeAllInfoApi();
                setCreateFormData({
                    name: emtpyString,
                    description: emtpyString,
                    priority: emtpyString,
                    status: emtpyString
                })
                setCreatemodal(falseValue);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            toast.error(error.response?.data?.message);

            // * if user is unauthorized
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        } finally {
            setDisable(falseValue);
            setLoading(falseValue);
        }
    };

    // * handle update 
    const handleUpdateInfo = (info) => {
        setUpdateFormData({
            id: info.id,
            name: info.name,
            description: info.description,
            status: info.status,
            priority: Number(info.priority)
        });
        setContent(info.description)
        setUpdateModal(trueValue);
    }

    // * execute update info
    const executeUpdateInfoApi = async (event) => {
        event.preventDefault();

        setLoading(trueValue);
        setDisable(trueValue);

        //* send data to request
        const data = {
            description: content,
            name: updateFormData.name,
            status: Number(updateFormData.status),
            priority: Number(updateFormData.priority)
        }

        try {
            const apiUrl = `${config.appBaseUrl + ALL_INFO_END_POINT}/${updateFormData.id}`;

            //* post request
            const response = await axios.put(apiUrl,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            //* response success
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                toast.success(result.message);
                executeAllInfoApi();
                setUpdateModal(falseValue);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            toast.error(error.response?.data?.message);

            // * if user is unauthorized
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        } finally {
            setDisable(falseValue);
            setLoading(falseValue);
        }
    };

    // * execute update info
    const executeDeleteInfoApi = async () => {

        setLoading(trueValue);
        setDisable(trueValue);

        try {
            const apiUrl = `${config.appBaseUrl + ALL_INFO_END_POINT}/${infoID}`;

            //* post request
            const response = await axios.delete(apiUrl,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            //* response success
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                toast.success(result.message);
                executeAllInfoApi();
                setConfirmationModal(falseValue);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            toast.error(error.response?.data?.message);

            // * if user is unauthorized
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        } finally {
            setDisable(falseValue);
            setLoading(falseValue);
        }
    };

    return (
        <>
            <ToastContainer autoClose={700} position='top-center' />

            {/* //* delete info confirmation model */}
            {confirmationModal && (
                <>
                    <div className='modal-backdrop fade show'></div>
                    <div
                        id='user_logout_modal'
                        className='modal fade show'
                        style={{ display: 'block' }}
                    >
                        <div className='modal-dialog'>
                            <div className='modal-content' ref={modalRef}>
                                <div className='modal-body'>
                                    <p className='mb-0'>Are you sure you want to delete this page?</p>
                                </div>
                                <div className='modal-footer me-2 border-top-0'>
                                    <button
                                        type='button'
                                        className='btn btn-danger text-white'
                                        data-dismiss='modal'
                                        onClick={() => setConfirmationModal(falseValue)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='button'
                                        className='btn btn-success text-white'
                                        onClick={executeDeleteInfoApi}
                                        disabled={disable}
                                    >
                                        {!loading ? 'Confirm' : <Loader />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}


            {/* //* create page modal */}
            {
                createmodal && (
                    <>
                        <div className='modal-backdrop show'></div>
                        <div
                            id='create_update_modal'
                            className='modal show'
                            tabIndex={-1}
                            role='dialog'
                            aria-labelledby='standard-modalLabel'
                            aria-modal='true'
                            style={{ display: 'block' }}
                        >
                            <div className='modal-dialog modal-dialog-centered modal-lg'>
                                <div className='modal-content' ref={modalRef}>
                                    <div className='modal-header vendor-bg-grey m-2' style={{ position: 'sticky' }}>
                                        <h5 className='modal-title '>Create Page</h5>
                                        <button
                                            type='button'
                                            className='btn-close vendor-icon text-dark'
                                            onClick={() => setCreatemodal(falseValue)}
                                        />
                                    </div>
                                    <div className='modal-body  pt-1' style={{ overflow: 'auto' }}>
                                        <form>
                                            <div className='row g-2'>
                                                {/* //* version input */}
                                                <div className='col-12 col-md-12 mb-2'>
                                                    <label htmlFor='name' className='form-label '>
                                                        Name
                                                    </label>
                                                    <input
                                                        type='text'
                                                        id='name'
                                                        className='form-control m-0'
                                                        placeholder='Enter page name'
                                                        value={createFormData.name}
                                                        onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                                                    />
                                                </div>

                                                {/* //* message input */}
                                                <div className='col-12 col-md-6 mb-2'>
                                                    <div className='mb-2'>
                                                        <label htmlFor='priority' className='form-label'>Priority</label>
                                                        <input
                                                            type='number'
                                                            className='form-control m-0'
                                                            id='priority'
                                                            placeholder='Enter priority'
                                                            min={ONE}
                                                            value={createFormData.priority}
                                                            onChange={(e) => setCreateFormData({ ...createFormData, priority: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                {/* //* message input */}
                                                <div className='col-12 col-md-6 mb-2'>
                                                    <label htmlFor='status_active_inactive' className='form-label'>Status</label>
                                                    <select
                                                        className='form-control m-0'
                                                        id='status_active_inactive'
                                                        value={createFormData.status}
                                                        onChange={(e) => setCreateFormData({ ...createFormData, status: Number(e.target.value) })}
                                                    >
                                                        <option value=''>Select Status</option>
                                                        <option value={1}>Active</option>
                                                        <option value={0}>Inactive</option>
                                                    </select>
                                                </div>

                                                {/* //* new features editor */}
                                                <div className='col-12 col-md-12 mb-2'>
                                                    <label htmlFor='description' className='form-label'>
                                                        Description
                                                    </label>
                                                    <JoditEditor
                                                        ref={editor}
                                                        value={content}
                                                        config={editorConfig}
                                                        tabIndex={1}
                                                        onBlur={newContent => setContent(newContent)}
                                                        onChange={(newContent) => setContent(newContent)}
                                                    />
                                                </div>
                                            </div>

                                            {/* //* modal footer button */}
                                            <div className='modal-footer pb-0 mt-2'>
                                                <button
                                                    type='button'
                                                    className='btn btn-danger text-white mb-0'
                                                    onClick={() => setCreatemodal(falseValue)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type='button'
                                                    className='btn btn-primary me-0 mb-0'
                                                    id='create_service'
                                                    onClick={executeCreateInformationApi}
                                                    disabled={disable}
                                                >
                                                    {!loading ? 'Add' : <Loader />}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }

            {/* //* update page modal */}
            {
                updateModal && (
                    <>
                        <div className='modal-backdrop show'></div>
                        <div
                            id='create_update_modal'
                            className='modal show'
                            tabIndex={-1}
                            role='dialog'
                            aria-labelledby='standard-modalLabel'
                            aria-modal='true'
                            style={{ display: 'block' }}
                        >
                            <div className='modal-dialog modal-dialog-centered modal-lg'>
                                <div className='modal-content' ref={modalRef}>
                                    <div className='modal-header vendor-bg-grey m-2' style={{ position: 'sticky' }}>
                                        <h5 className='modal-title '>Update Page</h5>
                                        <button
                                            type='button'
                                            className='btn-close vendor-icon text-dark'
                                            onClick={() => setUpdateModal(falseValue)}
                                        />
                                    </div>
                                    <div className='modal-body  pt-1' style={{ overflow: 'auto' }}>
                                        <form>
                                            <div className='row g-2'>
                                                {/* //* version input */}
                                                <div className='col-12 col-md-12 mb-2'>
                                                    <label htmlFor='name' className='form-label '>
                                                        Name
                                                    </label>
                                                    <input
                                                        type='text'
                                                        id='name'
                                                        className='form-control m-0'
                                                        placeholder='Enter page name'
                                                        value={updateFormData.name}
                                                        onChange={(e) => setUpdateFormData({ ...updateFormData, name: e.target.value })}
                                                    />
                                                </div>

                                                {/* //* message input */}
                                                <div className='col-12 col-md-6 mb-2'>
                                                    <div className='mb-2'>
                                                        <label htmlFor='priority' className='form-label'>Priority</label>
                                                        <input
                                                            type='number'
                                                            className='form-control m-0'
                                                            id='priority'
                                                            placeholder='Enter priority'
                                                            min={ONE}
                                                            value={updateFormData.priority}
                                                            onChange={(e) => setUpdateFormData({ ...updateFormData, priority: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                {/* //* message input */}
                                                <div className='col-12 col-md-6 mb-2'>
                                                    <label htmlFor='status_active_inactive' className='form-label'>Status</label>
                                                    <select
                                                        className='form-control m-0'
                                                        id='status_active_inactive'
                                                        value={updateFormData.status}
                                                        onChange={(e) => setUpdateFormData({ ...updateFormData, status: Number(e.target.value) })}
                                                    >
                                                        <option value=''>Select Status</option>
                                                        <option value={1}>Active</option>
                                                        <option value={0}>Inactive</option>
                                                    </select>
                                                </div>

                                                {/* //* new features editor */}
                                                <div className='col-12 col-md-12 mb-2'>
                                                    <label htmlFor='description' className='form-label'>
                                                        Description
                                                    </label>
                                                    <JoditEditor
                                                        ref={editor}
                                                        value={content}
                                                        config={editorConfig}
                                                        tabIndex={1}
                                                        onBlur={newContent => setContent(newContent)}
                                                        onChange={(newContent) => setContent(newContent)}
                                                    />
                                                </div>
                                            </div>

                                            {/* //* modal footer button */}
                                            <div className='modal-footer pb-0 mt-2'>
                                                <button
                                                    type='button'
                                                    className='btn btn-danger text-white mb-0'
                                                    onClick={() => setUpdateModal(falseValue)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type='button'
                                                    className='btn btn-primary me-0 mb-0'
                                                    id='create_service'
                                                    onClick={executeUpdateInfoApi}
                                                >
                                                    Update
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }

            <div className='page-content'>
                <div className='container-fluid'>
                    {/* //* start page title */}
                    <div className='row'>
                        <div className='col-12'>
                            <div className='page-title-box d-flex  align-items-start align-items-sm-center justify-content-between gap-2'>
                                <h4 className='mb-2 mb-sm-0'>Information</h4>
                                <div className='page-title-right'>
                                    <ol className='breadcrumb m-0'>
                                        <li className='breadcrumb-item'>
                                            <Link to='/dashboard'>Dashboard</Link>
                                        </li>
                                        <li className='breadcrumb-item active'>Information</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-lg-12'>
                            <div className='card'>
                                <div className='card-body'>
                                    {/* //* header buttons */}
                                    <div
                                        className='d-flex align-items-center justify-content-end mb-3'
                                        style={{ width: '100%' }}
                                    >
                                        <button type='button' className='btn btn-success' onClick={() => {
                                            setCreatemodal(!createmodal);
                                            setContent(emtpyString);
                                        }}>
                                            Add
                                        </button>
                                    </div>

                                    <div className='table-responsive'>
                                        <table className='w-100 text-sm text-left border-collapse custom-stripe-table '>
                                            <thead className='bg-gray-200  text-center'>
                                                <tr>
                                                    <th className='px-4 py-2'>SN</th>
                                                    <th className='px-4 py-2'>Name</th>
                                                    <th className='px-4 py-2'>Priority</th>
                                                    <th className='px-4 py-2'>Status</th>
                                                    <th className='px-4 py-2'>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    paginatedData.length > ZERO ?
                                                        paginatedData.map((info, index) => (
                                                            <tr className='bg-white hover:bg-gray-100' key={info.id}>
                                                                <td className='px-4 py-2'>{currentPage * REQUESTED_RECORD + (index + ONE)}</td>
                                                                <td className='px-4 py-2 text-nowrap'>{info.name}</td>
                                                                <td className='px-4 py-2'>{info.priority}</td>
                                                                <td className='px-4 py-2'>
                                                                    <span className={`badge ${info.status === ZERO ? 'badge-danger-lighten' : 'badge-success-lighten'}`}>{info.status === ZERO ? 'INACTIVE' : 'ACTIVE'}</span>
                                                                </td>
                                                                <td className='px-4 py-2'>
                                                                    <div className='d-flex align-items-center justify-content-center'>
                                                                        <RiPencilFill
                                                                            size={20}
                                                                            data-tooltip-id='update_information' data-tooltip-content='Update Information'
                                                                            className=' text-primary vendor-icon pointer user-status me-2'
                                                                            onClick={() => handleUpdateInfo(info)}
                                                                        />
                                                                        <Tooltip id='update_information' place='bottom' effect='solid' />

                                                                        <MdDelete
                                                                            size={20}
                                                                            data-tooltip-id='delele_information' data-tooltip-content='Delete Information'
                                                                            className=' text-danger vendor-icon pointer user-status'
                                                                            onClick={() => {
                                                                                setConfirmationModal(trueValue);
                                                                                setInfoID(info.id)
                                                                            }}
                                                                        />
                                                                        <Tooltip id='delele_information' place='bottom' effect='solid' />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                        :
                                                        <tr>
                                                            <td colSpan={5}>No data found!</td>
                                                        </tr>
                                                }
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* //* pagination */}
                                    {
                                        paginatedData.length > ZERO &&
                                        <div className='float-end'>
                                            <ReactPaginate
                                                previousLabel={<MdKeyboardArrowLeft className='fs-4' />}
                                                nextLabel={<MdKeyboardArrowRight className='fs-4' />}
                                                className='pagination'
                                                breakLabel={'...'}
                                                breakLinkClassName={'disabled-break'}
                                                pageCount={totalPages > ZERO ? totalPages : ONE}
                                                forcePage={(totalPages > ZERO) && (currentPage < totalPages) ? (currentPage) : ZERO}
                                                marginPagesDisplayed={TWO}
                                                pageRangeDisplayed={TWO}
                                                onPageChange={handlePageChange}
                                                containerClassName={'pagination'}
                                                subContainerClassName={'pages pagination'}
                                                activeClassName={'pagination-active-page'}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default AllInformation;